

































































































































import Vue from "vue";
import { createNamespacedHelpers } from "vuex";
import { Blog, BlogCategory, Business, Role } from "@/types";
import { VueEditor } from "vue2-editor";

const { mapActions: blogActions } = createNamespacedHelpers("BLOG_");
const { mapActions: categoryActions, mapGetters: categoryGetters } =
  createNamespacedHelpers("BLOG_CATEGORY_");

const API_URL = process.env.VUE_APP_API_URL;

interface VForm extends Vue {
  validate(): boolean;
  reset(): void;
}

export default Vue.extend({
  name: "BlogForm",
  components: {
    VueEditor,
  },
  props: {
    blog: {
      type: Object as () => Blog | null,
      default: null,
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
      appUrl: API_URL,
      featuredImage: null as File | null,
      previewImage: null as string | null,
      showEditorError: false,
      editorToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link", "image"],
        ["clean"],
      ],
      formData: {
        title: "",

        excerpt: "",
        message: "",
        categories: [] as string[],
        status: "published",
      },
      statusOptions: [
        { text: "Published", value: "published" },
        { text: "Draft", value: "draft" },
      ],
      titleRules: [
        (v: string) => !!v || "Title is required",
        (v: string) =>
          (v && v.length <= 100) || "Title must be less than 100 characters",
      ],
      slugRules: [
        (v: string) => !!v || "Slug is required",
        (v: string) =>
          (v && /^[a-z0-9-]+$/.test(v)) ||
          "Slug can only contain lowercase letters, numbers, and hyphens",
      ],
      excerptRules: [
        (v: string) =>
          !v || v.length <= 200 || "Excerpt must be less than 200 characters",
      ],
      messageRules: [(v: string) => !!v || "Content is required"],
    };
  },
  computed: {
    ...categoryGetters(["categoryPage"]),
    role(): Role {
      return this.$store.getters.role;
    },
    isEdit(): boolean {
      return !!this.blog;
    },
    categories(): BlogCategory[] {
      return this.categoryPage?.docs || [];
    },
  },
  methods: {
    ...blogActions(["createBlog", "updateBlog"]),
    ...categoryActions(["fetchCategoryList"]),
    previewImageFunc(file: File) {
      if (file) {
        this.previewImage = URL.createObjectURL(file);
      } else {
        this.previewImage = null;
      }
    },
    resetForm() {
      if (this.$refs.form) {
        (this.$refs.form as VForm).reset();
      }
      this.formData = {
        title: "",
        // slug: "",
        excerpt: "",
        message: "",
        categories: [],
        status: "published",
      };
      this.featuredImage = null;
      this.previewImage = null;
      this.showEditorError = false;
    },
    async submitForm() {
      this.showEditorError = !this.formData.message;

      if (!this.formData.message) {
        console.warn("Blog content is required");
        return;
      }

      if (this.$refs.form) {
        if (!(this.$refs.form as VForm).validate()) {
          console.warn("Form validation failed");
          return;
        }
      }

      try {
        this.loading = true;
        const formData = new FormData();

        // Add basic fields
        formData.append("title", this.formData.title);
        // formData.append("slug", this.formData.slug);
        formData.append("excerpt", this.formData.excerpt);
        formData.append("message", this.formData.message);
        formData.append("status", this.formData.status);

        console.log("Form data being submitted:", {
          title: this.formData.title,
          // slug: this.formData.slug,
          excerpt: this.formData.excerpt,
          message: this.formData.message.substring(0, 100) + "...", // Show first 100 chars of message
          status: this.formData.status,
          categories: this.formData.categories,
        });

        // Add categories
        if (this.formData.categories.length > 0) {
          for (const categoryId of this.formData.categories) {
            formData.append("categories[]", categoryId);
          }
        }

        // Add featured image if new one was selected
        if (this.featuredImage) {
          formData.append("featuredImage", this.featuredImage);
        }

        // Get business ID
        const businessId =
          typeof this.role.business === "string"
            ? this.role.business
            : (this.role.business as Business)._id;

        // Debug - verify FormData is properly built
        console.log(
          "FormData entries:",
          Array.from(formData.entries()).map(([key, value]) => {
            if (value instanceof File) {
              return [key, `File: ${value.name}`];
            }
            return [key, value];
          })
        );

        // Perform create or update
        if (this.isEdit && this.blog) {
          try {
            await this.updateBlog({
              businessId,
              blogId: this.blog._id,
              data: formData,
            });
          } catch (error) {
            console.error("Update blog error:", error);
            throw error;
          }
        } else {
          try {
            await this.createBlog({
              businessId,
              data: formData,
            });
          } catch (error) {
            console.error("Create blog error:", error);
            throw error;
          }
        }

        this.$emit("saved");
      } catch (error) {
        console.error("Error submitting form:", error);

        // Show error toast/notification
        this.$store.dispatch("setToast", {
          title: "Error",
          type: "error",
          text: "Failed to save blog post. Please try again.",
        });
      } finally {
        this.loading = false;
      }
    },
    populateForm() {
      if (this.blog) {
        this.formData = {
          title: this.blog.title || "",
          // slug: this.blog.slug || "",
          excerpt: this.blog.excerpt || "",
          message: this.blog.message || "",
          categories: this.blog.categories.map((cat) =>
            typeof cat === "string" ? cat : cat._id
          ),
          status: this.blog.status || "published",
        };
      }
    },
  },
  mounted() {
    if (this.role) {
      const businessId =
        typeof this.role.business === "string"
          ? this.role.business
          : (this.role.business as Business)._id;

      this.fetchCategoryList({ businessId });
    }

    if (this.isEdit) {
      this.populateForm();
    }
  },
});
